/**
 * Submission comments — `/v2/submissions/{id}/comment-threads/` and
 * `/v2/submissions/{id}/comments/{comment_id}` (backend OR-1535, PR #407;
 * contract in `orion-backend/docs/submission-comments.md`).
 *
 * Contract notes that matter to callers:
 *  - Starting a thread and replying to one are separate calls. Starting
 *    upserts the cell's open thread (so it never fails because one exists);
 *    replying targets a thread id and answers 409 once it is resolved, so a
 *    reply never silently lands in a fresh thread with none of its context.
 *  - Threads carry `num_comments` only; the comments themselves are a
 *    paginated list (50 per page) on the thread.
 *  - PATCH / DELETE of a comment: author only (403 otherwise); 409 once its
 *    thread is resolved. DELETE is a soft delete.
 *  - The thread list defaults to OPEN threads that still have live comments.
 *  - Resolve / Undo is one PATCH on the thread (`is_resolved`). Undo answers
 *    409 when a newer thread with live comments already sits on that cell.
 *  - `body` is trimmed server-side and must be 1–5000 chars.
 */
import type {
    CommentListPage,
    CommentPage,
    SubmissionCommentThreadWire,
    SubmissionCommentWire,
} from "../../types/submission-comment";
import { getJsonReal, sendJsonReal } from "../client";

export const MAX_COMMENT_LENGTH = 5000;

/** The backend's default and maximum page size for comment lists. */
const COMMENT_PAGE_SIZE = 50;

const base = (submissionId: string) => `/v2/submissions/${encodeURIComponent(submissionId)}`;
const threadUrl = (submissionId: string, threadId: string) => `${base(submissionId)}/comment-threads/${encodeURIComponent(threadId)}`;

/** The open thread on one cell, or null when the cell has none. */
export async function getOpenCommentThread(
    submissionId: string,
    page: CommentPage,
    field: string,
): Promise<SubmissionCommentThreadWire | null> {
    const qs = new URLSearchParams({ page, field });
    const { items } = await getJsonReal<CommentListPage<SubmissionCommentThreadWire>>(`${base(submissionId)}/comment-threads/?${qs}`);
    return items[0] ?? null;
}

/** Every live comment of a thread, oldest first. The backend pages by 50; a
 *  cell thread is rarely that long, but keep reading until `total`. */
export async function listCommentThreadComments(submissionId: string, threadId: string): Promise<SubmissionCommentWire[]> {
    const all: SubmissionCommentWire[] = [];
    for (let offset = 0; ; ) {
        const qs = new URLSearchParams({ offset: String(offset), limit: String(COMMENT_PAGE_SIZE) });
        const { total, items } = await getJsonReal<CommentListPage<SubmissionCommentWire>>(`${threadUrl(submissionId, threadId)}/comments/?${qs}`);
        all.push(...items);
        offset += items.length;
        if (items.length === 0 || offset >= total) return all;
    }
}

/** Starts the cell's thread with `body` as its first comment. When an open
 *  thread already sits on the anchor the backend joins it instead, so this
 *  is safe when the caller has no thread to reply to. Returns the thread
 *  (counts only, no comments). */
export function startCommentThread(
    submissionId: string,
    anchor: { page: CommentPage; field: string },
    body: string,
): Promise<SubmissionCommentThreadWire> {
    return sendJsonReal(`${base(submissionId)}/comment-threads/`, "POST", { ...anchor, body });
}

/** Adds a comment to a known open thread; 409 once the thread is resolved. */
export function replyToCommentThread(submissionId: string, threadId: string, body: string): Promise<SubmissionCommentWire> {
    return sendJsonReal(`${threadUrl(submissionId, threadId)}/comments/`, "POST", { body });
}

export function updateSubmissionComment(submissionId: string, commentId: string, body: string): Promise<SubmissionCommentWire> {
    return sendJsonReal(`${base(submissionId)}/comments/${encodeURIComponent(commentId)}`, "PATCH", { body });
}

export function deleteSubmissionComment(submissionId: string, commentId: string): Promise<void> {
    return sendJsonReal(`${base(submissionId)}/comments/${encodeURIComponent(commentId)}`, "DELETE");
}

/** `true` resolves the thread, `false` reopens it (Undo). */
export function setCommentThreadResolved(
    submissionId: string,
    threadId: string,
    isResolved: boolean,
): Promise<SubmissionCommentThreadWire> {
    return sendJsonReal(threadUrl(submissionId, threadId), "PATCH", { is_resolved: isResolved });
}
